import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import Button from '../common/Button';
import { supabase } from '../../utils/supabase';
import {
  FormSection,
  FormField,
  Select,
  FormActions
} from '../common/Form';
import SessionConfigFields from './SessionConfigFields';
import {
  DEFAULT_SESSION_START_TIME,
  DEFAULT_SESSION_END_TIME,
  getDefaultEndTime,
} from '../../utils/sessionTimeUtils';
import { MATCH_FORMAT_DOUBLES } from '../../utils/competitiveMatchFormat';
import { isClubPlannableSessionType } from '../../utils/sessionTypeUtils';
import {
  PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  normalizePegBoardQueueReturn,
} from '../../utils/pegBoard/constants';

const PLANNING_OPTIONAL_STATUSES = ['ongoing', 'completed', 'archived'];
const planningFieldsRequired = (status) => !PLANNING_OPTIONAL_STATUSES.includes(status);

const formatTimeFromMinutes = (totalMinutes) =>
  `${Math.floor(totalMinutes / 60).toString().padStart(2, '0')}:${(totalMinutes % 60).toString().padStart(2, '0')}`;

const SessionTemplateForm = ({  
  onSubmit, 
  onCancel, 
  initialData = null, 
  loading = false,
  error = null,
  clubId,
  isModal = false,
  mode = 'template', // 'template' or 'session'
  creationMode = 'quick', // 'quick', 'customize', 'adhoc' - for session mode
  hideActions = false, // New prop to hide action buttons
  scrollToPricing = false, // New prop to auto-scroll to pricing section
  collapsibleSections = false,
}) => {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedBaseTemplate, setSelectedBaseTemplate] = useState(null);
  const pricingSectionRef = useRef(null);
  const [formData, setFormData] = useState({
    session_type_id: '',
    session_name: '',
    name: '', // For session mode
    description: '',
    max_players: '',
    number_of_courts: '1',
    price_per_player: '',
    court_booking_cost: '',
    location: '',
    min_elo_level: '',
    start_time: DEFAULT_SESSION_START_TIME,
    session_time: DEFAULT_SESSION_START_TIME, // For session mode
    end_time: DEFAULT_SESSION_END_TIME,
    session_date: '', // For session mode
    day_of_week: '0',
    is_recurring: false,
    is_active: true,
    session_status: 'draft', // For session mode
    // New fields for enhanced templates
    signup_deadline_hours: '',
    waitlist_enabled: true,
    waitlist_promotion_type: 'first_come_first_served',
    signup_selection: 'first_come',
    is_public: false,
    currency_code: 'GBP',
    game_type_restriction: 'balanced_only',
    enable_player_categorisation: false,
    competitive_match_format: MATCH_FORMAT_DOUBLES,
    points_target: 21,
    deuce_mode: 'standard',
    peg_board_selection_window: 6,
    peg_board_queue_return: PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
    session_series_id: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const sessionNameFieldKey = mode === 'session' ? 'name' : 'session_name';
  const startTimeFieldKey = mode === 'session' ? 'session_time' : 'start_time';

  // Session config value for shared SessionConfigFields (map formData to unified shape)
  const sessionConfigValue = {
    ...formData,
    name: mode === 'session' ? formData.name : formData.session_name,
    start_time: mode === 'session' ? formData.session_time : formData.start_time
  };
  const sessionConfigOnChange = useCallback((next) => {
    setFormData((prev) => ({
      ...prev,
      ...next,
      name: mode === 'session' ? next.name : prev.name,
      session_name: mode === 'template' ? next.name : prev.session_name,
      session_time: mode === 'session' ? next.start_time : prev.session_time,
      start_time: mode === 'template' ? next.start_time : prev.start_time
    }));
  }, [mode]);

  // Load session types on mount
  useEffect(() => {
    fetchSessionTypes();
  }, []);

  // Load templates when in session mode or template mode
  useEffect(() => {
    if (clubId && (mode === 'session' || mode === 'template')) {
      fetchTemplates();
    }
  }, [mode, clubId]);

  // Initialize form with data if editing
  useEffect(() => {
    if (initialData) {
      // Strip seconds from time values (convert HH:MM:SS to HH:MM)
      const startTime = initialData.start_time ? initialData.start_time.substring(0, 5) : '';
      const endTime = initialData.end_time ? initialData.end_time.substring(0, 5) : '';
      const sessionTime = initialData.session_time ? initialData.session_time.substring(0, 5) : '';
      
      setFormData({
        session_type_id: initialData.session_type_id || '',
        session_name: initialData.session_name || '',
        name: initialData.name || initialData.session_name || '', // For session mode
        description: initialData.description || '',
        max_players: initialData.max_players ?? '',
        number_of_courts:
          initialData.number_of_courts != null && initialData.number_of_courts !== ''
            ? String(initialData.number_of_courts)
            : (planningFieldsRequired(initialData.session_status) ? '1' : ''),
        price_per_player: initialData.price_per_player || '',
        court_booking_cost: initialData.court_booking_cost || '',
        location: initialData.location || '',
        min_elo_level: initialData.min_elo_level || '',
        start_time: startTime,
        session_time: sessionTime || startTime, // For session mode
        end_time: endTime,
        session_date: initialData.session_date || '', // For session mode
        day_of_week: initialData.day_of_week?.toString() || '0',
        is_recurring: initialData.is_recurring || false,
        is_active: initialData.is_active !== false,
        // New fields for enhanced templates
        signup_deadline_hours: initialData.signup_deadline_hours || '',
        waitlist_enabled: initialData.waitlist_enabled !== false,
        waitlist_promotion_type: initialData.waitlist_promotion_type || 'first_come_first_served',
        signup_selection: initialData.signup_selection || 'first_come',
        is_public: initialData.is_public || false,
        currency_code: initialData.currency_code || 'GBP',
        session_status: initialData.session_status || 'upcoming',
        game_type_restriction: initialData.game_type_restriction || 'balanced_only',
        enable_player_categorisation: initialData.enable_player_categorisation || false,
        competitive_match_format: initialData.competitive_match_format || MATCH_FORMAT_DOUBLES,
        points_target: initialData.points_target ?? 21,
        deuce_mode: initialData.deuce_mode || 'standard',
        peg_board_selection_window: initialData.peg_board_selection_window ?? 6,
        peg_board_queue_return: normalizePegBoardQueueReturn(initialData.peg_board_queue_return),
        session_series_id: initialData.session_series_id || '',
      });
    }
  }, [initialData]);

  // Scroll to pricing section when requested
  useEffect(() => {
    if (scrollToPricing && pricingSectionRef.current) {
      // Small delay to ensure the modal and form are fully rendered
      const timer = setTimeout(() => {
        pricingSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        
        // Focus on the price input field
        const priceInput = document.querySelector('input[name="price_per_player"]');
        if (priceInput) {
          priceInput.focus();
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [scrollToPricing]);

  const fetchSessionTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('session_types')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setSessionTypes(data || []);
    } catch (err) {
      console.error('Error fetching session types:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('club_session_setup')
        .select(`
          *,
          session_types (
            id,
            name
          )
        `)
        .eq('club_id', clubId)
        .eq('is_active', true)
        .order('session_name');
      
      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'radio' ? value === 'true' : value)
    }));
  };

  // Specific handlers for time inputs
  const handleStartTimeChange = (value) => {
    const fieldName = mode === 'session' ? 'session_time' : 'start_time';
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleEndTimeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      end_time: value
    }));
  };

  const handleBaseTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedBaseTemplate(template);
      // Populate form with template data
      setFormData(prev => ({
        ...prev,
        session_type_id: template.session_type_id || '',
        session_name: template.session_name || '',
        description: template.description || '',
        max_players: template.ideal_player_count || '',
        number_of_courts: template.number_of_courts || '1',
        price_per_player: template.price_per_player || '',
        court_booking_cost: template.court_booking_cost || '',
        location: template.location || '',
        min_elo_level: template.min_elo_level || '',
        start_time: template.start_time ? template.start_time.substring(0, 5) : '',
        end_time: template.end_time ? template.end_time.substring(0, 5) : getDefaultEndTime(
          template.start_time ? template.start_time.substring(0, 5) : DEFAULT_SESSION_START_TIME
        ),
        signup_deadline_hours: template.signup_deadline_hours || '',
        waitlist_enabled: template.waitlist_enabled || false,
        waitlist_promotion_type: template.waitlist_promotion_type || 'first_come_first_served',
        signup_selection: template.signup_selection || 'first_come',
        is_public: template.is_public || false,
        currency_code: template.currency_code || 'USD',
        game_type_restriction: template.game_type_restriction || 'balanced_only',
        enable_player_categorisation: template.enable_player_categorisation || false,
        competitive_match_format: template.competitive_match_format || MATCH_FORMAT_DOUBLES,
        points_target: template.points_target ?? 21,
        deuce_mode: template.deuce_mode || 'standard',
        peg_board_selection_window: template.peg_board_selection_window ?? 6,
        peg_board_queue_return: normalizePegBoardQueueReturn(template.peg_board_queue_return),
        day_of_week: template.day_of_week || '',
        is_recurring: template.is_recurring || false,
        is_active: template.is_active || false,
        session_series_id: template.session_series_id || '',
      }));
    }
  };

  // Function to get the next occurrence of a specific day of the week
  const getNextDateForDayOfWeek = (targetDayOfWeek) => {
    const today = new Date();
    const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate days until the target day
    let daysUntilTarget = targetDayOfWeek - currentDayOfWeek;
    
    // If the target day is today or has passed this week, get next week's occurrence
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7;
    }
    
    // Create the target date
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysUntilTarget);
    
    // Format as YYYY-MM-DD for the date input
    return targetDate.toISOString().split('T')[0];
  };

  const timeStringToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return (hours * 60) + minutes;
  };

  const evaluateRequiredFields = useCallback((data) => {
    const errors = {};
    const missing = [];

    const addError = (key, label, message) => {
      errors[key] = message || `${label} is required`;
      if (!missing.includes(label)) {
        missing.push(label);
      }
    };

    if (!data.session_type_id) {
      addError('session_type_id', 'Session Type');
    }

    const sessionNameKey = mode === 'session' ? 'name' : 'session_name';
    const sessionNameValue = data[sessionNameKey];
    if (!sessionNameValue || !sessionNameValue.trim()) {
      addError(sessionNameKey, 'Session Name');
    }

    const startTimeKey = mode === 'session' ? 'session_time' : 'start_time';
    const requirePlanning = planningFieldsRequired(data.session_status);

    if (requirePlanning && !data[startTimeKey]) {
      addError(startTimeKey, 'Start Time');
    }

    if (requirePlanning && !data.end_time) {
      addError('end_time', 'End Time');
    }

    if (mode === 'template' && (!data.day_of_week || data.day_of_week === '')) {
      addError('day_of_week', 'Day of Week');
    }

    if (mode === 'session' && !data.session_date) {
      addError('session_date', 'Session Date');
    }

    if (requirePlanning && (!data.max_players || Number(data.max_players) <= 0)) {
      addError('max_players', 'Max Players', 'Max Players must be greater than 0');
    }

    if (requirePlanning && (!data.number_of_courts || Number(data.number_of_courts) <= 0)) {
      addError('number_of_courts', 'Number of Courts', 'Number of Courts must be greater than 0');
    }

    // Validate end time is after start time
    const startTimeValue = data[startTimeKey];
    const endTimeValue = data.end_time;
    if (startTimeValue && endTimeValue) {
      const startMinutes = timeStringToMinutes(startTimeValue);
      const endMinutes = timeStringToMinutes(endTimeValue);
      if (endMinutes <= startMinutes) {
        addError('end_time', 'End Time', 'End time must be after start time');
      }
      const durationMinutes = endMinutes - startMinutes;
      if (durationMinutes > 1440) { // 24 hours
        addError('end_time', 'End Time', 'Session duration cannot exceed 24 hours');
      }
    }

    return { errors, missing };
  }, [mode]);

  useEffect(() => {
    const { errors, missing } = evaluateRequiredFields(formData);
    setFieldErrors(errors);
    setMissingFields(missing);
  }, [formData, evaluateRequiredFields]);

  const prepareSessionData = () => {
    const sessionName = mode === 'session' ? formData.name : formData.session_name;
    const startTime = mode === 'session' ? formData.session_time : formData.start_time;
    const requirePlanning = planningFieldsRequired(formData.session_status);

    const startMinutes = startTime ? timeStringToMinutes(startTime) : null;
    const endMinutes = formData.end_time ? timeStringToMinutes(formData.end_time) : null;

    if (requirePlanning && (startMinutes == null || endMinutes == null)) {
      throw new Error('Please provide start and end times');
    }

    if (startMinutes != null && endMinutes != null) {
      const durationMinutes = endMinutes - startMinutes;
      if (durationMinutes <= 0) {
        throw new Error('End time must be after start time');
      }
      if (durationMinutes > 1440) {
        throw new Error('Session duration cannot exceed 24 hours');
      }
    }

    const setupData = {
      club_id: clubId,
      session_type_id: formData.session_type_id,
      description: formData.description ? formData.description.trim() || null : null,
      max_players: formData.max_players ? parseInt(formData.max_players) : null,
      number_of_courts: formData.number_of_courts
        ? parseInt(formData.number_of_courts, 10)
        : (requirePlanning ? 1 : null),
      price_per_player: formData.price_per_player ? parseFloat(formData.price_per_player) : null,
      court_booking_cost: formData.court_booking_cost ? parseFloat(formData.court_booking_cost) : null,
      location: formData.location ? formData.location.trim() || null : null,
      min_elo_level: formData.min_elo_level ? parseInt(formData.min_elo_level) : null,
      end_time: endMinutes != null ? formatTimeFromMinutes(endMinutes) : null,
      signup_deadline_hours: formData.signup_deadline_hours ? parseInt(formData.signup_deadline_hours) : null,
      waitlist_enabled: true,
      waitlist_promotion_type: formData.waitlist_promotion_type,
      signup_selection: formData.signup_selection === 'host_select' ? 'host_select' : 'first_come',
      is_public: formData.is_public,
      currency_code: formData.currency_code,
      game_type_restriction: formData.game_type_restriction,
      enable_player_categorisation: formData.enable_player_categorisation,
      competitive_match_format: formData.competitive_match_format || MATCH_FORMAT_DOUBLES,
      points_target: formData.points_target ?? 21,
      deuce_mode: formData.deuce_mode || 'standard',
      peg_board_selection_window: formData.peg_board_selection_window ?? 6,
      peg_board_queue_return: normalizePegBoardQueueReturn(formData.peg_board_queue_return),
      session_series_id: formData.session_series_id || null,
    };

    // Add mode-specific fields
    if (mode === 'template') {
      setupData.session_name = formData.session_name ? formData.session_name.trim() : '';
      setupData.ideal_player_count = formData.max_players ? parseInt(formData.max_players) : null;
      setupData.number_of_courts = formData.number_of_courts ? parseInt(formData.number_of_courts, 10) : 1;
      setupData.start_time = startMinutes != null ? formatTimeFromMinutes(startMinutes) : null;
      setupData.day_of_week = parseInt(formData.day_of_week);
      setupData.is_recurring = formData.is_recurring;
      setupData.is_active = formData.is_active;
    } else {
      // Session mode
      setupData.name = formData.name ? formData.name.trim() : '';
      if (startMinutes != null) {
        setupData.start_time = formatTimeFromMinutes(startMinutes);
      }
      setupData.session_date = formData.session_date;
      setupData.session_status = formData.session_status;
      setupData.created_by = formData.created_by; // Add created_by for session creation
    }

    return setupData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { errors, missing } = evaluateRequiredFields(formData);
    setFieldErrors(errors);
    setMissingFields(missing);

    if (missing.length > 0) {
      setShowValidationSummary(true);
      return;
    }

    setShowValidationSummary(false);
    
    try {
      const setupData = prepareSessionData();
      await onSubmit(setupData);
    } catch (err) {
      throw err; // Let parent handle the error
    }
  };


  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  // Enhanced styling with modern design principles
  const formClass = isModal ? "space-y-6" : "space-y-8";
  const sectionClass = isModal ? "space-y-4" : "space-y-6";
  const inputClass = isModal 
    ? "w-full border-2 border-border-medium rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary bg-surface text-text-primary transition-all duration-300 hover:border-border-medium hover:shadow-sm"
    : "w-full border-2 border-border-medium rounded-xl px-5 py-4 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary bg-surface text-text-primary shadow-sm transition-all duration-300 hover:border-border-medium hover:shadow-md";
  const labelClass = isModal 
    ? "block text-sm font-semibold text-text-primary mb-2"
    : "block text-sm font-bold text-text-primary mb-3";
  const cardClass = isModal 
    ? "bg-surface rounded-xl border border-border-medium p-6 shadow-sm"
    : "bg-surface rounded-2xl border border-border-medium p-8 shadow-lg hover:shadow-xl transition-all duration-300";

  return (
    <form id="session-template-form" onSubmit={handleSubmit} noValidate className={formClass}>
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-danger-light border-2 border-danger/30 rounded-xl shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-danger" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-danger-dark text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Template Selection - session mode only */}
      {(mode === 'session' && !initialData) && (
        <FormSection title="Start with a template (optional)">
          <FormField
            label="Template"
            hint="Templates save time by pre-filling common session settings. You can create templates in the Templates section below."
          >
            <Select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                setSelectedTemplate(template);
                if (template) {
                  let sessionDate = new Date().toISOString().split('T')[0];
                  if (template.day_of_week !== undefined) {
                    sessionDate = getNextDateForDayOfWeek(template.day_of_week);
                  }
                  setFormData(prev => ({
                    ...prev,
                    name: template.session_name || '',
                    description: template.description || '',
                    max_players: template.ideal_player_count || '',
                    number_of_courts: template.number_of_courts || '1',
                    price_per_player: template.price_per_player || '',
                    court_booking_cost: template.court_booking_cost || '',
                    location: template.location || '',
                    min_elo_level: template.min_elo_level || '',
                    session_time: template.start_time ? template.start_time.substring(0, 5) : '',
                    end_time: template.end_time ? template.end_time.substring(0, 5) : getDefaultEndTime(
          template.start_time ? template.start_time.substring(0, 5) : DEFAULT_SESSION_START_TIME
        ),
                    session_date: sessionDate,
                    signup_deadline_hours: template.signup_deadline_hours || '',
                    waitlist_enabled: template.waitlist_enabled !== undefined ? template.waitlist_enabled : true,
                    waitlist_promotion_type: template.waitlist_promotion_type || 'first_come_first_served',
                    signup_selection: template.signup_selection || 'first_come',
                    is_public: template.is_public !== undefined ? template.is_public : false,
                    session_type_id: template.session_type_id || '',
                    currency_code: template.currency_code || 'GBP',
                    game_type_restriction: template.game_type_restriction || 'balanced_only',
                    enable_player_categorisation: template.enable_player_categorisation || false,
                    competitive_match_format: template.competitive_match_format || MATCH_FORMAT_DOUBLES,
                    points_target: template.points_target ?? 21,
                    deuce_mode: template.deuce_mode || 'standard',
                    peg_board_selection_window: template.peg_board_selection_window ?? 6,
                    peg_board_queue_return: normalizePegBoardQueueReturn(template.peg_board_queue_return),
                    session_series_id: template.session_series_id || '',
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    name: '',
                    description: '',
                    max_players: '',
                    number_of_courts: '1',
                    price_per_player: '',
                    court_booking_cost: '',
                    location: '',
                    min_elo_level: '',
                    session_time: DEFAULT_SESSION_START_TIME,
                    end_time: getDefaultEndTime(DEFAULT_SESSION_START_TIME),
                    session_date: new Date().toISOString().split('T')[0],
                    signup_deadline_hours: '',
                    waitlist_enabled: true,
                    waitlist_promotion_type: 'first_come_first_served',
                    signup_selection: 'first_come',
                    is_public: false,
                    session_type_id: '',
                    currency_code: 'GBP',
                    game_type_restriction: 'balanced_only',
                    enable_player_categorisation: false,
                    competitive_match_format: MATCH_FORMAT_DOUBLES,
                    session_series_id: '',
                  }));
                }
              }}
              name="template_id"
              placeholder="Start from scratch"
              options={templates.map(template => ({
                value: template.id,
                label: `${template.session_name} (${template.session_types?.name || 'Unknown Type'})`
              }))}
            />
          </FormField>
        </FormSection>
      )}

      {/* Base Template Selection - template mode only */}
      {mode === 'template' && templates.length > 0 && (
        <FormSection title="Start from Existing Template (Optional)">
          <FormField label="Template" helperText="Select a template to pre-fill the form with its settings">
            <Select
              value={selectedBaseTemplate?.id || ''}
              onChange={(e) => handleBaseTemplateSelect(e.target.value)}
              placeholder="Start from scratch"
              options={templates.map(template => ({
                value: template.id,
                label: `${template.session_name} (${template.session_types?.name || 'Unknown Type'})`
              }))}
            />
          </FormField>
        </FormSection>
      )}

      <div ref={pricingSectionRef}>
        <SessionConfigFields
          value={sessionConfigValue}
          onChange={sessionConfigOnChange}
          clubId={clubId}
          sessionTypes={sessionTypes}
          fieldErrors={fieldErrors}
          isModal={isModal}
          showSessionTypeDescription={!isModal}
          showSessionStatus={mode === 'session'}
          showMakeActive={!isModal && mode === 'template'}
          nameLabel="Session Name"
          filterSessionTypes={isClubPlannableSessionType}
          requirePlanningFields={planningFieldsRequired(formData.session_status)}
          collapsibleSections={collapsibleSections}
          sessionDateProps={
            mode === 'session'
              ? {
                  value: formData.session_date,
                  onChange: handleInputChange,
                  error: fieldErrors.session_date,
                  min: format(new Date(), 'yyyy-MM-dd'),
                }
              : null
          }
          dayOfWeekProps={
            mode === 'template'
              ? {
                  value: formData.day_of_week,
                  onChange: handleInputChange,
                  error: fieldErrors.day_of_week,
                  options: daysOfWeek.map((day) => ({ value: String(day.value), label: day.label })),
                }
              : null
          }
        />
      </div>

      {showValidationSummary && missingFields.length > 0 && (
        <div className="mb-4 p-3 bg-danger-light border border-danger/30 rounded-lg text-sm text-danger-dark">
          Please complete: {missingFields.join(', ')}
        </div>
      )}

      {/* Actions - conditionally rendered */}
      {!hideActions && (
        <>
          <FormActions>
            {onCancel && (
              <Button
                type="button"
                onClick={onCancel}
                variant="outline"
                size="large"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={loading || missingFields.length > 0}
              variant="primary"
              size="large"
            >
              {loading ? 'Saving...' : (mode === 'session' ? (initialData ? 'Update Session' : 'Create Session') : (initialData ? 'Update Template' : 'Create Template'))}
            </Button>
          </FormActions>
          {missingFields.length > 0 && (
            <p className="text-xs text-danger-dark mt-2 text-center">Missing mandatory fields</p>
          )}
        </>
      )}
    </form>
  );
};

export default SessionTemplateForm;